/**
 * The usage log vocabulary: the one file on the frontend side that holds the names.
 *
 * The usage log records what people do in I2Q, to improve the product. This file says
 * WHAT the browser may report: the event names, the route templates, and for each name
 * the detail keys with their value type. `track()` in `lib/usage.ts` takes only these
 * names, and `checkEvent` refuses every detail outside the list before anything is
 * queued. The server checks the same list again (`backend/src/rfq_foundry/
 * usage_vocabulary.py`); `openapi/usage-vocabulary.json` is its checked-in copy and
 * `frontend/tests/usage-capture.test.mjs` compares the two, so they cannot drift.
 *
 * There is no free-text type and no open id type. A detail value is a boolean, a small
 * whole number, a route template, an area code or one word of a fixed list. An id of the
 * code (a help topic, a menu entry, a language, a dashboard tab, the first part of a
 * request key, a module or a guide) is one word of such a list too. So the log cannot
 * hold mail text, a customer name, a search word or the text of a dialog.
 *
 * No import from React or the API.
 */

export const USAGE_KINDS = ["sign_in", "page_view", "page_leave", "ui", "error", "perf"] as const;
export type UsageKind = (typeof USAGE_KINDS)[number];

export const USAGE_CLIENTS = ["desktop", "phone", "outlook_pane"] as const;
export type UsageClient = (typeof USAGE_CLIENTS)[number];

/**
 * The route templates, in the order of the pages. A path the app does not know is
 * `/other`. The query string and the hash are never part of a route.
 */
export const USAGE_ROUTES = [
  "/queue",
  "/case",
  "/case/:id",
  "/addin",
  "/learned",
  "/training",
  "/training/quick-start",
  "/training/team",
  "/training/managers",
  "/training/:module",
  "/rules/countries",
  "/rules/glossary",
  "/rules/how-it-works",
  "/rules/live",
  "/rules/:guide",
  "/feedback",
  "/feedback/triage",
  "/feedback/:id",
  "/dashboard",
  "/dashboard/report",
  "/help",
  "/docs",
  "/discussions",
  "/discussions/:id",
  "/roadmap",
  "/usage",
  "/cost",
  "/master-data",
  "/platform",
  "/decisions",
  "/process-mining",
  "/learn-from-sales",
  "/extraction-training",
  "/other",
] as const;
export type UsageRoute = (typeof USAGE_ROUTES)[number];

/** The dialogs with an `open`, a `save` and a `cancel` event each. */
export type UsageDialog =
  | "set_price"
  | "pick_item"
  | "pick_customer"
  | "give_feedback"
  | "correct_quantity"
  | "add_line";

/** What a call may hand to `track()`. A `null` or `undefined` value drops its key. */
export type UsageDetail = Record<string, boolean | number | string | null | undefined>;

/**
 * The closed lists that stand where an id of the code is recorded. A test
 * (`frontend/tests/usage-capture.test.mjs`) keeps each list equal to its source, and the
 * backend holds the same lists (`backend/src/rfq_foundry/usage_vocabulary.py`).
 */

/** The Help topics (`HelpTopicId` in `features/help/content.ts`). */
export const USAGE_HELP_TOPICS = [
  "training",
  "what-it-is",
  "mail-to-quotation",
  "customer",
  "case-page",
  "case-does-not-go-through",
  "work-faster",
  "back-for-you",
  "prepared-replies",
  "i2q-decided-lines",
  "deeper-search",
  "translation",
  "photos",
  "queue",
  "statuses",
  "learned",
  "dashboard",
  "not-yet",
  "contact",
  "usage-log",
  "feedback",
  "rules",
  "rules-and-countries",
  "cost-and-runs",
  "master-data",
  "what-runs-today",
  "business-decisions",
  "process-mining",
  "menu",
] as const;

/** The entries of the line menu, the customer menu and "More" on a case. */
export const USAGE_MENU_ITEMS = [
  "pick",
  "change-item",
  "correct-qty",
  "undo-price",
  "take-back-approval",
  "approve-price",
  "undo",
  "no-item",
  "look-again",
  "find_customer",
  "change_customer",
  "not_them",
  "try_again",
  "reopen_as_inquiry",
  "reopen",
  "mark_done",
  "next_case",
  "not_for_us",
  "pick_one_by_one",
  "no_item",
  "add_line",
  "approve_price",
  "waiting_customer",
  "copy_anyway",
  "edit_text",
  "undo_edits",
  "done_and_next",
  "copy_again",
  "take",
  "take_over",
  "release",
  "shortcuts",
] as const;

/** The languages of a mail translation (`TranslationTarget` of the API). */
export const USAGE_LANGUAGES = ["en", "da", "de", "fr", "es", "cs"] as const;

/** The dashboard tabs (`DashboardTab` in `features/dashboard/period.ts`). */
export const USAGE_DASHBOARD_TABS = ["summary", "operations", "system", "my-area"] as const;

/**
 * The first part of every query and mutation key: what a failed request read or wrote.
 * A key outside the list is recorded without `what`.
 */
export const USAGE_REQUEST_KEYS = [
  "areas",
  "case",
  "case-answer-draft",
  "case-answer-draft-create",
  "case-by-message",
  "case-catalogue",
  "case-crm-machines",
  "case-customer-memory",
  "case-customers",
  "case-events",
  "case-feedback",
  "case-fo-draft",
  "case-installed-base",
  "case-mail",
  "case-quotation-approval",
  "case-translation",
  "case-write",
  "data-health",
  "discussions",
  "engine-status",
  "extraction-training",
  "feedback-hub",
  "health",
  "learned-proposals",
  "learning-stats",
  "master-data-defects",
  "me",
  "me-attention",
  "metrics-alerts",
  "metrics-autonomy",
  "metrics-cost",
  "metrics-cost-summary",
  "metrics-customer-matching",
  "metrics-executive",
  "metrics-model-health",
  "metrics-ops",
  "metrics-pilot",
  "metrics-runs",
  "metrics-usage",
  "metrics-workload",
  "office-item",
  "platform-status",
  "process-mining-areas",
  "process-mining-baseline",
  "process-mining-case",
  "process-mining-cases",
  "process-mining-findings",
  "process-mining-map",
  "process-mining-report-status",
  "process-mining-runs",
  "process-mining-summary",
  "process-mining-tools",
  "process-mining-value",
  "process-mining-variants",
  "queue",
  "rules",
  "rules-table",
  "sales-truth-inquiries",
  "sales-truth-inquiry",
  "sales-truth-learning-write",
  "sales-truth-learnings",
  "sales-truth-people",
  "sales-truth-summary",
  "sales-truth-trail",
  "settings-margin-thresholds",
  "training-progress",
  "training-team",
] as const;

/**
 * The modules of the sales training, then the country guides and their short address
 * forms: the one id of `/training/:module` and `/rules/:guide`.
 */
export const USAGE_PAGE_IDS = [
  "welcome",
  "queue",
  "customer",
  "lines",
  "prices",
  "reply",
  "status",
  "extras",
  "quiz",
  "closing",
  "denmark",
  "france",
  "germany",
  "india",
  "north-america",
  "spain-mexico-czechia",
  "united-kingdom",
  "czechia",
  "czech-republic",
  "spain",
  "mexico",
] as const;

/** The type string of a fixed list of words, as `openapi/usage-vocabulary.json` has it. */
function enumOf(words: readonly string[]): string {
  return "enum:" + words.join("|");
}

/** The shape of an area code (an entity or a role code); the server checks the registry. */
const AREA_RE = /^[a-z]{2,4}$/;
/** Every "int" detail value is 0 to this. */
export const USAGE_INT_MAX = 1_000_000;
/** The one longer whole number: a time in milliseconds (one day). */
export const USAGE_DURATION_MAX_MS = 86_400_000;
/** Most detail keys of one event. */
export const USAGE_DETAIL_MAX_KEYS = 8;

const INT_MAX_BY_KEY: Record<string, number> = { wall_ms: USAGE_DURATION_MAX_MS };

/** The upper limit of a whole-number detail value of this key. */
export function usageIntMax(key: string): number {
  return INT_MAX_BY_KEY[key] ?? USAGE_INT_MAX;
}

// The detail type strings are the strings of `openapi/usage-vocabulary.json`.
const VIEW =
  "enum:work|ready|review|unidentified|replied|waiting-customer|closed|not-rfq|parked|waiting|all|could-not-process|portal";
const PRIMARY =
  "enum:reading|set_status|nothing|take_over|find_customer|confirm_customer|choose_customer|accept|confirm_lines|pick|approve_price|check|prepare|prepare_again|done_and_next|copy|close_not_an_inquiry";
const SECTION = "enum:checks|customer|quotation|order-system|documents|related|history|feedback";
const MENU = "enum:line|customer|more";
const NAV = "enum:load|route";
const HELP_TOPIC = enumOf(USAGE_HELP_TOPICS);
const MENU_ITEM = enumOf(USAGE_MENU_ITEMS);
const LANGUAGE = enumOf(USAGE_LANGUAGES);
const DASHBOARD_TAB = enumOf(USAGE_DASHBOARD_TABS);
const REQUEST_KEY = enumOf(USAGE_REQUEST_KEYS);
const PAGE_ID = enumOf(USAGE_PAGE_IDS);
/** The kinds of a master-data defect (`MasterDataKind` of the API), in their rank order. */
const MASTER_DATA_KIND =
  "enum:item_hsn_missing|price_missing|item_no_lead_time|customer_twin_accounts|customer_inactive_in_fo|customer_do_not_use|item_blocked_here|customer_address_missing|item_not_in_entity|item_name_missing";

interface EventShape {
  kind: UsageKind;
  detail: Readonly<Record<string, string>>;
  /** May carry a case id. */
  case: boolean;
  /** May carry `duration_ms`. */
  duration: boolean;
}

const DIALOG_OPEN = { kind: "ui", detail: {}, case: true, duration: false } as const;
const DIALOG_CLOSE = { kind: "ui", detail: {}, case: true, duration: true } as const;

/**
 * Every name a browser may report, in the order of the vocabulary. The one server-only
 * name (`auth.sign_in`) is not here: the server writes it.
 */
export const USAGE_EVENTS = {
  "page.view": {
    kind: "page_view",
    detail: { from: "route", nav: NAV, slug: PAGE_ID },
    case: true,
    duration: false,
  },
  "page.leave": {
    kind: "page_leave",
    detail: { reason: "enum:route|hidden|close", wall_ms: "int" },
    case: true,
    duration: true,
  },
  "perf.page_load": {
    kind: "perf",
    detail: { ttfb_ms: "int", load_ms: "int" },
    case: false,
    duration: true,
  },
  "perf.page_ready": { kind: "perf", detail: { nav: NAV }, case: true, duration: true },
  "error.read": {
    kind: "error",
    detail: { status: "int", what: REQUEST_KEY },
    case: true,
    duration: false,
  },
  "error.write": {
    kind: "error",
    detail: { status: "int", what: REQUEST_KEY },
    case: true,
    duration: false,
  },
  "error.page": {
    kind: "error",
    detail: { status: "int", shown: "enum:page|card" },
    case: true,
    duration: false,
  },
  "error.script": {
    kind: "error",
    detail: { source: "enum:error|rejection" },
    case: false,
    duration: false,
  },
  "queue.search": {
    kind: "ui",
    detail: { len: "int", numeric: "bool", view: VIEW },
    case: false,
    duration: false,
  },
  "queue.search_empty": {
    kind: "ui",
    detail: { len: "int", numeric: "bool", view: VIEW },
    case: false,
    duration: false,
  },
  "queue.view": { kind: "ui", detail: { view: VIEW }, case: false, duration: false },
  "queue.chip": {
    kind: "ui",
    detail: {
      chip: "enum:area|back_for_me|over_1_working_day|older|all_dates|from_colleagues|closed_no_reply_seen|closed_today|closed_all",
      areas: "int",
      area: "area",
    },
    case: false,
    duration: false,
  },
  "queue.filters_open": { kind: "ui", detail: { active: "int" }, case: false, duration: false },
  "queue.filter_change": {
    kind: "ui",
    detail: {
      filter:
        "enum:assignee|days|mailbox|area|sender|type|dates|addressed|internal|threads|unmapped",
    },
    case: false,
    duration: false,
  },
  "queue.filters_reset": {
    kind: "ui",
    detail: { action: "enum:default|everyone_any_date" },
    case: false,
    duration: false,
  },
  "queue.page": { kind: "ui", detail: { page: "int" }, case: false, duration: false },
  "queue.open_case": {
    kind: "ui",
    detail: { view: VIEW, position: "int", page: "int" },
    case: true,
    duration: false,
  },
  "queue.bulk": {
    kind: "ui",
    detail: { action: "enum:done|not_for_us|waiting_customer|take|open|release", count: "int" },
    case: false,
    duration: false,
  },
  "shortcut.use": {
    kind: "ui",
    detail: {
      action:
        "enum:next|previous|accept|confirmCustomer|take|doneAndNext|copyReply|rowConfirm|rowNoItem|rowPrice|rowQuantity|rowPick|help|select|open",
    },
    case: true,
    duration: false,
  },
  "case.entry_state": {
    kind: "ui",
    detail: { action: PRIMARY, lines: "int", from_queue: "bool" },
    case: true,
    duration: false,
  },
  "case.primary_action": { kind: "ui", detail: { action: PRIMARY }, case: true, duration: false },
  "case.mail_unfold": {
    kind: "ui",
    detail: { part: "enum:read_all|earlier" },
    case: true,
    duration: false,
  },
  "case.attachment_open": {
    kind: "ui",
    detail: { kind: "enum:file|photo_where|photo_whole" },
    case: true,
    duration: false,
  },
  "case.translation": {
    kind: "ui",
    detail: { action: "enum:request|show_original|show_translation|language", lang: LANGUAGE },
    case: true,
    duration: false,
  },
  "case.why_open": {
    kind: "ui",
    detail: { what: "enum:customer|proposal|rule" },
    case: true,
    duration: false,
  },
  "case.line_details": { kind: "ui", detail: {}, case: true, duration: false },
  "case.details_switch": { kind: "ui", detail: { on: "bool" }, case: true, duration: false },
  "case.details_open": {
    kind: "ui",
    detail: {
      section:
        "enum:checks|customer|quotation|order-system|documents|related|history|feedback|none",
    },
    case: true,
    duration: false,
  },
  "case.sheet_section": { kind: "ui", detail: { section: SECTION }, case: true, duration: false },
  "case.menu_open": { kind: "ui", detail: { menu: MENU }, case: true, duration: false },
  "case.menu_item": {
    kind: "ui",
    detail: { menu: MENU, item: MENU_ITEM },
    case: true,
    duration: false,
  },
  "case.reply_copy": {
    kind: "ui",
    detail: { prepared: "bool", edited: "bool" },
    case: true,
    duration: false,
  },
  "case.lines_copy": { kind: "ui", detail: { lines: "int" }, case: true, duration: false },
  "case.next": { kind: "ui", detail: { dir: "enum:next|previous" }, case: true, duration: false },
  "case.proposals_shown": {
    kind: "ui",
    detail: { customer: "int", lines: "int" },
    case: true,
    duration: false,
  },
  "dialog.set_price.open": DIALOG_OPEN,
  "dialog.set_price.save": DIALOG_CLOSE,
  "dialog.set_price.cancel": DIALOG_CLOSE,
  "dialog.pick_item.open": DIALOG_OPEN,
  "dialog.pick_item.save": DIALOG_CLOSE,
  "dialog.pick_item.cancel": DIALOG_CLOSE,
  "dialog.pick_customer.open": DIALOG_OPEN,
  "dialog.pick_customer.save": DIALOG_CLOSE,
  "dialog.pick_customer.cancel": DIALOG_CLOSE,
  "dialog.give_feedback.open": DIALOG_OPEN,
  "dialog.give_feedback.save": DIALOG_CLOSE,
  "dialog.give_feedback.cancel": DIALOG_CLOSE,
  "dialog.correct_quantity.open": DIALOG_OPEN,
  "dialog.correct_quantity.save": DIALOG_CLOSE,
  "dialog.correct_quantity.cancel": DIALOG_CLOSE,
  "dialog.add_line.open": DIALOG_OPEN,
  "dialog.add_line.save": DIALOG_CLOSE,
  "dialog.add_line.cancel": DIALOG_CLOSE,
  "help.topic": {
    kind: "ui",
    detail: { topic: HELP_TOPIC, via: "enum:page|popup" },
    case: false,
    duration: false,
  },
  "content.search": {
    kind: "ui",
    detail: { where: "enum:help|docs", len: "int", hits: "int" },
    case: false,
    duration: false,
  },
  "content.print": {
    kind: "ui",
    detail: { what: "enum:docs|report|certificate" },
    case: false,
    duration: false,
  },
  "dashboard.view": {
    kind: "ui",
    detail: { tab: DASHBOARD_TAB, change: "enum:tab|period|area|weeks" },
    case: false,
    duration: false,
  },
  "cost.view": {
    kind: "ui",
    detail: { tab: "enum:cost|runs|health|alerts", change: "enum:tab|hours" },
    case: false,
    duration: false,
  },
  "addin.open_in_i2q": { kind: "ui", detail: {}, case: true, duration: false },
  // The pages "Master data", "What runs today" and "Business decisions" (15 Oct 2026).
  "master_data.view": {
    kind: "ui",
    detail: { change: "enum:open|area|kind|status|quiet" },
    case: false,
    duration: false,
  },
  "master_data.mark": {
    kind: "ui",
    detail: { status: "enum:open|in_progress|not_a_defect", kind: MASTER_DATA_KIND },
    case: false,
    duration: false,
  },
  "master_data.export": { kind: "ui", detail: { rows: "int" }, case: false, duration: false },
  "platform.view": { kind: "ui", detail: {}, case: false, duration: false },
  "decisions.view": {
    kind: "ui",
    detail: { change: "enum:open|status" },
    case: false,
    duration: false,
  },
  "decisions.frame": { kind: "ui", detail: {}, case: false, duration: false },
  // The page "Process mining" (15 Oct 2026): the tab opened, never a filter value. pm8
  // (17 Oct 2026): the tab "Core model".
  "process_mining.view": {
    kind: "ui",
    detail: {
      tab: "enum:findings|value_stream|map|areas|tools|case|baseline|runs|why|core_model|areas_compared",
    },
    case: false,
    duration: false,
  },
  // The report download (17 Oct 2026): the audience and the format, never an area.
  "process_mining.report_download": {
    kind: "ui",
    detail: {
      audience: "enum:steering_group|sales_leaders|area|process_it",
      format: "enum:docx|pptx",
    },
    case: false,
    duration: false,
  },
  // pm11 (17 Oct 2026, PM4-23): the CSV of the case list; no detail, never a filter value.
  "process_mining.case_csv": { kind: "ui", detail: {}, case: false, duration: false },
} as const satisfies Record<string, EventShape>;

export type UsageEventName = keyof typeof USAGE_EVENTS;

/** The three names of each dialog, so no name is ever built from parts. */
export const USAGE_DIALOG_EVENTS = {
  set_price: {
    open: "dialog.set_price.open",
    save: "dialog.set_price.save",
    cancel: "dialog.set_price.cancel",
  },
  pick_item: {
    open: "dialog.pick_item.open",
    save: "dialog.pick_item.save",
    cancel: "dialog.pick_item.cancel",
  },
  pick_customer: {
    open: "dialog.pick_customer.open",
    save: "dialog.pick_customer.save",
    cancel: "dialog.pick_customer.cancel",
  },
  give_feedback: {
    open: "dialog.give_feedback.open",
    save: "dialog.give_feedback.save",
    cancel: "dialog.give_feedback.cancel",
  },
  correct_quantity: {
    open: "dialog.correct_quantity.open",
    save: "dialog.correct_quantity.save",
    cancel: "dialog.correct_quantity.cancel",
  },
  add_line: {
    open: "dialog.add_line.open",
    save: "dialog.add_line.save",
    cancel: "dialog.add_line.cancel",
  },
} as const satisfies Record<
  UsageDialog,
  { open: UsageEventName; save: UsageEventName; cancel: UsageEventName }
>;

/** The shape of one name, or `undefined` for a name outside the vocabulary. */
export function usageEvent(name: string): EventShape | undefined {
  return Object.hasOwn(USAGE_EVENTS, name) ? USAGE_EVENTS[name as UsageEventName] : undefined;
}

/** The path as a list of its parts; the query string and the hash are never read. */
function partsOf(pathname: string): string[] {
  const path = (pathname.split(/[?#]/)[0] ?? "").trim();
  return path.split("/").filter((part) => part !== "");
}

const TRAINING_PAGES: Record<string, UsageRoute> = {
  "quick-start": "/training/quick-start",
  team: "/training/team",
  managers: "/training/managers",
};
const RULES_PAGES: Record<string, UsageRoute> = {
  countries: "/rules/countries",
  glossary: "/rules/glossary",
  "how-it-works": "/rules/how-it-works",
  live: "/rules/live",
};
const ONE_PART: Record<string, UsageRoute> = {
  queue: "/queue",
  case: "/case",
  addin: "/addin",
  learned: "/learned",
  training: "/training",
  feedback: "/feedback",
  dashboard: "/dashboard",
  help: "/help",
  docs: "/docs",
  discussions: "/discussions",
  roadmap: "/roadmap",
  usage: "/usage",
  cost: "/cost",
  "master-data": "/master-data",
  platform: "/platform",
  decisions: "/decisions",
  "process-mining": "/process-mining",
  "learn-from-sales": "/learn-from-sales",
  "extraction-training": "/extraction-training",
};
/** Pages that only send the person on, and the sign-in page: not tracked. */
const NOT_TRACKED = new Set(["", "home", "back", "rules", "login"]);

/**
 * The route template of a path: `/case/RFQ-1` is `/case/:id`. `null` means "not
 * tracked" (`/`, `/home`, `/back`, `/rules` and `/login`). Any other path the app does
 * not know is `/other`.
 */
export function routeTemplate(pathname: string): UsageRoute | null {
  const parts = partsOf(pathname);
  const first = (parts[0] ?? "").toLowerCase();
  if (parts.length <= 1) {
    if (NOT_TRACKED.has(first)) {
      return null;
    }
    return ONE_PART[first] ?? "/other";
  }
  // One case of the process mining (`/process-mining/cases/:caseKey`) is recorded as the
  // page itself: the case key is never part of a route.
  if (
    first === "process-mining" &&
    parts.length === 3 &&
    (parts[1] ?? "").toLowerCase() === "cases"
  ) {
    return "/process-mining";
  }
  if (parts.length > 2) {
    return "/other";
  }
  const second = (parts[1] ?? "").toLowerCase();
  switch (first) {
    case "case":
      return "/case/:id";
    case "training":
      return TRAINING_PAGES[second] ?? "/training/:module";
    case "rules":
      return RULES_PAGES[second] ?? "/rules/:guide";
    case "feedback":
      return second === "triage" ? "/feedback/triage" : "/feedback/:id";
    case "dashboard":
      return second === "report" ? "/dashboard/report" : "/other";
    case "discussions":
      return "/discussions/:id";
    default:
      return "/other";
  }
}

/** The case id of a `/case/:id` path, decoded; `null` on every other page. */
export function caseIdOf(pathname: string): string | null {
  if (routeTemplate(pathname) !== "/case/:id") {
    return null;
  }
  const raw = partsOf(pathname)[1] ?? "";
  try {
    return decodeURIComponent(raw) || null;
  } catch {
    return raw || null;
  }
}

/**
 * The module id of `/training/:module` or the guide id of `/rules/:guide`; `null` on
 * every other page, and for a part of the address that is no module and no guide. So a
 * part that a person typed is never recorded.
 */
export function slugOf(pathname: string): string | null {
  const route = routeTemplate(pathname);
  if (route !== "/training/:module" && route !== "/rules/:guide") {
    return null;
  }
  return usageWord(USAGE_PAGE_IDS, (partsOf(pathname)[1] ?? "").toLowerCase()) ?? null;
}

/**
 * What the log keeps of a search: how long the text is and whether it looks like a
 * number. Never a word of it.
 */
export function searchShape(text: string): { len: number; numeric: boolean } {
  const trimmed = text.trim();
  return { len: trimmed.length, numeric: /^[\d\s.\-/]+$/.test(trimmed) };
}

/** An area code in the shape the log takes, else `undefined` (the key is then left out). */
export function usageArea(code: string | null | undefined): string | undefined {
  return typeof code === "string" && AREA_RE.test(code) ? code : undefined;
}

/**
 * An id of the code when it is a word of its closed list, else `undefined` (the key is
 * then left out, the event is still recorded).
 */
export function usageWord<T extends string>(
  words: readonly T[],
  id: string | null | undefined,
): T | undefined {
  return typeof id === "string" && (words as readonly string[]).includes(id)
    ? (id as T)
    : undefined;
}

function valueFits(type: string, value: boolean | number | string, key: string): boolean {
  if (type === "bool") {
    return typeof value === "boolean";
  }
  if (type === "int") {
    return (
      typeof value === "number" &&
      Number.isInteger(value) &&
      value >= 0 &&
      value <= usageIntMax(key)
    );
  }
  if (typeof value !== "string") {
    return false;
  }
  if (type === "route") {
    return (USAGE_ROUTES as readonly string[]).includes(value);
  }
  if (type === "area") {
    return AREA_RE.test(value);
  }
  if (type.startsWith("enum:")) {
    return type.slice("enum:".length).split("|").includes(value);
  }
  return false;
}

/**
 * The detail as it is sent, or `null` when the event must be refused: a name outside the
 * vocabulary, more than eight keys, a key outside the list of the name, or a value of
 * the wrong type. A `null` or `undefined` value drops its key. The same rules as the
 * server's `check_detail`. No type accepts free text.
 */
export function checkEvent(name: string, detail: UsageDetail): UsageDetail | null {
  const event = usageEvent(name);
  if (!event || detail === null || typeof detail !== "object" || Array.isArray(detail)) {
    return null;
  }
  const keys = Object.keys(detail);
  if (keys.length > USAGE_DETAIL_MAX_KEYS) {
    return null;
  }
  const checked: UsageDetail = {};
  for (const key of keys) {
    const type = Object.hasOwn(event.detail, key) ? event.detail[key] : undefined;
    if (type === undefined) {
      return null;
    }
    const value = detail[key];
    if (value === null || value === undefined) {
      continue;
    }
    if (!valueFits(type, value, key)) {
      return null;
    }
    checked[key] = value;
  }
  return checked;
}
